import type { ReactNode } from 'react'
import { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import moment from 'moment'
import styled from 'styled-components'
import { rem } from 'polished'
import TripDrawer from 'tripPlanner/components/GenericDrawer/TripDrawer'
import ModalContext from 'contexts/ModalContext'
import JourneyV2DetailsModal from 'components/Flights/V2/FareFamily/JourneyV2DetailsModal'
import FlightSearchWidgetStateProvider from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateProvider'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import flightSearchWidgetStateReducer, {
  createEmptyFlightSearchWidgetFlightItem,
  initialState,
} from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import type { FlightSearchWidgetState, FlightSearchWidgetDispatchAction } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import FlightSearchWidgetDesktop from 'components/Flights/FlightsSearch/SearchWidget/FlightSearchWidgetDesktop'
import FlightSearchWidgetMobile from 'components/Flights/FlightsSearch/SearchWidget/FlightSearchWidgetMobile'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import useSearchFlights from 'hooks/useSearchFlights'
import type { FlightSearchParams } from 'hooks/useSearchFlights'
import { FlightSegment, FlightSortOptions, FlightsFareTypes, fareClassToUrlMap, FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES } from 'constants/flight'
import { INTL_DAY_MONTH_NAME, ISO_DATE_FORMAT } from 'constants/dateFormats'
import { resetFlightsTimer, hasFlightSessionExpired } from 'components/Flights/FlightsSessionTimer/FlightsSessionTimer'
import { FlightSessionCountdownClock } from 'components/Common/FlightSessionCountdownClock/FlightSessionCountdownClock'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { getTotalStopCount } from 'lib/flights/flightUtils'
import type { TransportOption } from 'tripPlanner/api/types'
import { useAppSelector } from 'hooks/reduxHooks'
import { cacheShortlistedJourneys } from 'tripPlanner/pages/chat/ChatTripPlannerPage/components/ShortlistedFlightsSection/shortlistedJourneyCache'
import ChatFlightResultsHeader from 'tripPlanner/components/Chat/Flights/ChatFlightResultsHeader'
import useChatFlightResultsSorting from 'tripPlanner/components/Chat/Flights/useChatFlightResultsSorting'
import { getSegmentKey, getSegmentKeys, getSearchItinerary } from 'tripPlanner/components/Chat/Flights/flightSegmentUtils'
import { buildMultiCityFlightMessage } from 'tripPlanner/components/Chat/Flights/multiCityFlightMessage'
import { useTripFlightFilters } from 'tripPlanner/components/Chat/Flights/useTripFlightFilters'
import TripFlightFiltersPanel from 'tripPlanner/components/Chat/Flights/TripFlightFiltersPanel'
import DrawerFlightCard from 'tripPlanner/components/Chat/Flights/DrawerFlightCard'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FlightsLoadingTopBar from 'components/Flights/V2/FlightsLoadingTopBar'
import LoadingAnimationBox from 'components/Common/LoadingAnimationBox/LoadingAnimationBox'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'
import StepperProgressSteps from 'LuxKit/Steppers/StepperProgressSteps'
import StepperProgressStep from 'LuxKit/Steppers/StepperProgressStep'
import type { StepClickHandler } from 'LuxKit/Steppers/StepperProgressStep'
import uuidV4 from 'lib/string/uuidV4Utils'

const SearchWrapper = styled.div`
  padding: ${rem(12)} ${rem(24)};
  max-width: ${rem(1080)};
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
`

const ContentWrapper = styled.div`
  padding: ${rem(24)};
  max-width: ${rem(1080)};
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
`

const CountdownWrapper = styled.div`
  padding: ${rem(8)} ${rem(24)};
  box-sizing: border-box;
`

const EMPTY_ITINERARY = [{ departureAirport: '', arrivalAirport: '', departureDate: '' }]

const EMPTY_SEARCH_PARAMS: FlightSearchParams = {
  itinerary: EMPTY_ITINERARY,
  occupancies: [{ adults: 1 }],
  fareClass: 'economy',
}

function hasCompleteFlightSearchDefaults({
  originAirportCode,
  destinationAirportCode,
  departureDate,
  returnDate,
  itinerary,
  fareType,
}: Pick<Props, 'originAirportCode' | 'destinationAirportCode' | 'departureDate' | 'returnDate' | 'itinerary' | 'fareType'>): boolean {
  if (itinerary?.length && fareType === FlightsFareTypes.MULTI_CITY) {
    return itinerary.every(leg => Boolean(leg.departureAirport && leg.arrivalAirport && leg.departureDate))
  }

  if (fareType === FlightsFareTypes.RETURN) {
    return Boolean(originAirportCode && destinationAirportCode && departureDate && returnDate)
  }

  return Boolean(originAirportCode && destinationAirportCode && departureDate)
}

interface Props {
  isOpen: boolean
  onClose: () => void
  originAirportCode?: string
  originAirportName?: string
  destinationAirportCode?: string
  destinationAirportName?: string
  departureDate?: string
  returnDate?: string
  itinerary?: FlightSearchParams['itinerary']
  fareType?: string
  fareClass?: string
  occupants?: App.Occupants
  passengers?: number
  autoSearch?: boolean
  onSendMessage?: (message: string) => void
  isStreaming?: boolean
  onCartFlightSelect?: (selection: FlightSearchDrawerCartSelection) => Promise<boolean | void> | boolean | void
  isCartSubmitting?: boolean
  existingFlights?: Array<TransportOption>
  /** Rendered under the "Saved" tab of the drawer (e.g. shortlisted flights). */
  headerSlot?: ReactNode
  /** Whether there are saved/shortlisted flights to surface under the "Saved" tab. */
  hasSavedFlights?: boolean
  showMultiCity?: boolean
}

export interface FlightSearchDrawerCartSelection {
  departingJourney: App.JourneyV2
  returningJourney?: App.JourneyV2
  multiCityJourneys?: Array<App.JourneyV2>
  searchParams: FlightSearchParams
  searchId: string | undefined
  sessionId: string | undefined
  searchExpiry: number
}

interface ContentProps {
  onSendMessage?: (message: string) => void
  isStreaming: boolean
  onCartFlightSelect?: (selection: FlightSearchDrawerCartSelection) => Promise<boolean | void> | boolean | void
  isCartSubmitting: boolean
  onClose: () => void
  existingFlightFingerprints: Set<string>
  autoSearch?: boolean
  headerSlot?: ReactNode
  hasSavedFlights?: boolean
  showMultiCity?: boolean
}

function getFlightFingerprint(flightNumber: string | undefined, departureAirport: string | undefined, arrivalAirport: string | undefined): string {
  return `${flightNumber ?? ''}|${departureAirport ?? ''}|${arrivalAirport ?? ''}`
}

function isJourneyAlreadyAdded(journey: App.JourneyV2, fingerprints: Set<string>): boolean {
  const flights = journey.flightGroup.flights
  const firstFlight = flights[0]
  const lastFlight = flights[flights.length - 1]
  return fingerprints.has(getFlightFingerprint(firstFlight.flightNumber, firstFlight.departureAirport, lastFlight.arrivalAirport))
}

function FlightSearchDrawerContent({
  onSendMessage,
  onCartFlightSelect,
  isCartSubmitting,
  onClose,
  existingFlightFingerprints,
  autoSearch,
  headerSlot,
  hasSavedFlights,
  showMultiCity,
  isStreaming,
}: ContentProps) {
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()
  const showModal = useContext(ModalContext)
  const isCartMode = !!onCartFlightSelect
  const canSelectFlight = isCartMode || !!onSendMessage
  const [searchParams, setSearchParams] = useState<FlightSearchParams | undefined>(undefined)
  const [hasSearched, setHasSearched] = useState(false)
  const [searchVersion, setSearchVersion] = useState(0)

  const [activeSegmentKey, setActiveSegmentKey] = useState<string>(FlightSegment.DEPARTING)
  const [flightSort, setFlightSort] = useState<FlightSortOptions>(FlightSortOptions.Best)
  const [selectedFlights, setSelectedFlights] = useState<Record<string, App.JourneyV2>>({})
  const [selectedFareFamilies, setSelectedFareFamilies] = useState<Record<string, App.FlightFareFamily>>({})

  const widgetState = useContext(FlightSearchWidgetStateContext)
  const { flights, occupants, fareType, fareCabin } = widgetState

  const isReturn = fareType?.value === FlightsFareTypes.RETURN
  const isMultiCity = fareType?.value === FlightsFareTypes.MULTI_CITY
  const defaultSegmentCount = isReturn ? 2 : Math.max(1, flights.length)
  const segments = useMemo<Array<string>>(() => getSegmentKeys(searchParams?.itinerary.length ?? defaultSegmentCount, isReturn), [defaultSegmentCount, isReturn, searchParams?.itinerary.length])
  const activeSegment = segments.includes(activeSegmentKey)
    ? activeSegmentKey
    : segments[0] ?? activeSegmentKey
  const isReturningStep = activeSegmentKey === FlightSegment.RETURNING

  const departDate = useMemo(() => {
    const flight = flights[0]
    return flight?.checkinDate ? moment(flight.checkinDate).format(ISO_DATE_FORMAT) : undefined
  }, [flights])

  const returnDate = useMemo(() => {
    const flight = flights[0]
    return flight?.checkoutDate ? moment(flight.checkoutDate).format(ISO_DATE_FORMAT) : undefined
  }, [flights])

  const handleSearch = useCallback(() => {
    const itinerary = getSearchItinerary(flights, fareType?.value)
    if (!itinerary) {
      return
    }

    const nextVersion = searchVersion + 1
    const params: FlightSearchParams = {
      itinerary,
      occupancies: [occupants],
      fareClass: fareCabin?.value ? fareClassToUrlMap[fareCabin.value] ?? 'economy' : 'economy',
      fareType: fareType?.value,
      key: nextVersion,
    }

    setSearchVersion(nextVersion)
    setSearchParams(params)
    setHasSearched(true)
    setActiveSegmentKey(getSegmentKey(0, fareType?.value === FlightsFareTypes.RETURN))
    setSelectedFlights({})
    setSelectedFareFamilies({})
  }, [flights, occupants, fareType, fareCabin, searchVersion])

  const hasAutoSearched = useRef(false)
  useEffect(() => {
    if (autoSearch && !hasAutoSearched.current) {
      hasAutoSearched.current = true
      handleSearch()
    }
  }, [autoSearch, handleSearch])

  const { journeys, fetching, error, searchResultsTimestamp, key: searchKey } = useSearchFlights(
    searchParams ?? EMPTY_SEARCH_PARAMS,
    {
      disabled: !searchParams,
      segments,
      activeSegmentKey: activeSegment,
      selectedFlights,
      selectedFareFamilies,
    },
  )

  const cachedSearchId = useAppSelector(state => state.flights.searchV2Flights[searchKey]?.searchId)
  const cachedSessionId = useAppSelector(state => state.flights.searchV2Flights[searchKey]?.sessionId)

  const displayJourneys = useChatFlightResultsSorting({
    journeys,
    activeSegmentKey: activeSegment,
    sort: flightSort,
    departDate,
    returnDate,
    selectedFlights,
  })

  // Filter the sorted, same-day-aware results. Filter options are built from
  // `displayJourneys` so they stay stable as the user narrows the selection;
  // filters reset whenever the search or active segment changes.
  const tripFlightFilters = useTripFlightFilters(displayJourneys, {
    resetKey: `${searchKey}-${activeSegment}`,
  })
  const { visibleJourneys, resetAllFilters } = tripFlightFilters

  const expiryTimestamp = useMemo(() => {
    if (!searchResultsTimestamp) return 0
    return moment(searchResultsTimestamp).add(FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES, 'minutes').toDate().getTime()
  }, [searchResultsTimestamp])

  useEffect(() => {
    if (searchResultsTimestamp) {
      resetFlightsTimer(expiryTimestamp)
    }
  }, [expiryTimestamp, searchResultsTimestamp])

  const handleSearchRef = useRef(handleSearch)
  handleSearchRef.current = handleSearch

  useEffect(() => {
    if (!hasSearched || fetching || !expiryTimestamp) return

    if (hasFlightSessionExpired()) {
      handleSearchRef.current()
      return
    }

    const remaining = expiryTimestamp - Date.now()
    if (remaining <= 0) {
      handleSearchRef.current()
      return
    }

    const timeout = setTimeout(() => {
      handleSearchRef.current()
    }, remaining)

    return () => clearTimeout(timeout)
  }, [hasSearched, fetching, expiryTimestamp])

  const currentSegmentIndex = useMemo(() => {
    return Math.max(0, segments.findIndex(s => s === activeSegment))
  }, [segments, activeSegment])

  const handleStepClick = useCallback<StepClickHandler>((stepIndex) => {
    setActiveSegmentKey(segments[stepIndex])
    // Keep the clicked step's own selection; only clear the legs after it (their
    // choices may depend on this leg, and the user is re-opening this step to
    // review/change it, not to lose it).
    const keysToRemove = segments.slice(stepIndex + 1)
    setSelectedFlights(prev => {
      const next = { ...prev }
      for (const key of keysToRemove) delete next[key]
      return next
    })
    setSelectedFareFamilies(prev => {
      const next = { ...prev }
      for (const key of keysToRemove) delete next[key]
      return next
    })
  }, [segments])

  const getStepSublabel = useCallback((journey: App.JourneyV2) => {
    const flight = journey.flightGroup.flights[0]
    const date = dateFormatter(flight.departureDateTime, INTL_DAY_MONTH_NAME)
    return `${date} · ${flight.departingTime}`
  }, [dateFormatter])

  const buildFlightContext = useCallback((journey: App.JourneyV2) => {
    const flights = journey.flightGroup.flights
    const firstFlight = flights[0]
    const lastFlight = flights[flights.length - 1]
    const duration = journey.flightGroup.totalFlightDuration
    const lines = [
      `Carrier: ${journey.carrierName}`,
      `Flight: ${flights.map(f => f.flightNumber).join(' / ')}`,
      `From: ${firstFlight.departureAirport} (${firstFlight.departureAirportName})`,
      `To: ${lastFlight.arrivalAirport} (${lastFlight.arrivalAirportName})`,
      `Departure: ${firstFlight.departureDateTime}`,
      `Arrival: ${lastFlight.arrivalDateTime}`,
      `Duration: ${duration.hours}h ${duration.minutes}m`,
      `Stops: ${getTotalStopCount(flights)}`,
      `Price: ${journey.currencyCode} ${journey.price.adult.totalFare}`,
      `Fare type: ${journey.fareType}`,
      `Booking key: ${journey.bookingInfo.boookingKey}`,
      `Search ID: ${journey.bookingInfo.providerSearchId}`,
      `Journey Key: ${journey.bookingInfo.journeyKey}`,
      `Journey ID: ${journey.id}`,
      `Carrier logo: ${journey.carrierBrandMark ?? journey.carrierLogo}`,
    ]
    if (journey.regularPrice?.all?.totalFare !== undefined) lines.push(`Regular price: ${journey.currencyCode} ${journey.regularPrice.all.totalFare}`)
    if (journey.regularPrice?.adult?.totalFare !== undefined) lines.push(`Regular price per person: ${journey.currencyCode} ${journey.regularPrice.adult.totalFare}`)
    if (journey.bookingInfo?.isLuxPlusFare !== undefined) lines.push(`LuxPlus fare: ${journey.bookingInfo.isLuxPlusFare}`)
    return lines.join('\n')
  }, [])

  const handleSelectFlight = useCallback(async(data: { journey: App.JourneyV2 }) => {
    const currentSegmentKey = activeSegment
    const currentSegmentIndex = segments.findIndex(key => key === currentSegmentKey)

    if (currentSegmentIndex < segments.length - 1) {
      setSelectedFlights(prev => ({ ...prev, [currentSegmentKey]: data.journey }))
      setActiveSegmentKey(segments[currentSegmentIndex + 1])
      return
    }

    const departingJourney = selectedFlights.departing
    const returningJourney = isReturn ? data.journey : undefined
    const selectedJourneyList = [
      ...segments.slice(0, -1).map(segment => selectedFlights[segment]),
      data.journey,
    ].filter((journey): journey is App.JourneyV2 => !!journey)

    const outbound = selectedJourneyList[0] ?? departingJourney ?? data.journey
    const outboundFlights = outbound.flightGroup.flights
    const from = outboundFlights[0].departureAirport
    const finalJourney = selectedJourneyList[selectedJourneyList.length - 1] ?? outbound
    const finalFlights = finalJourney.flightGroup.flights
    const to = finalFlights[finalFlights.length - 1].arrivalAirport

    const cacheSelectedJourneys = () => {
      let journeysToCache: Array<App.JourneyV2> = [data.journey]
      if (isMultiCity) {
        journeysToCache = selectedJourneyList
      } else if (isReturn && departingJourney && returningJourney) {
        journeysToCache = [departingJourney, returningJourney]
      }
      // The drawer search runs through `useSearchFlights` without a LuxPlus
      // request mode, so these journeys are public-priced. Cache them as such
      // so a charge-eligible member doesn't reuse this public searchId at book
      // time (their booking paths force a fresh member-priced search instead).
      cacheShortlistedJourneys(journeysToCache, cachedSearchId, cachedSessionId, false)
    }

    if (isCartMode) {
      if (!onCartFlightSelect || !searchParams || isCartSubmitting || isStreaming) return
      const didSelect = await Promise.resolve(onCartFlightSelect({
        departingJourney: outbound,
        returningJourney,
        multiCityJourneys: isMultiCity ? selectedJourneyList : undefined,
        searchParams,
        searchId: cachedSearchId,
        sessionId: cachedSessionId,
        searchExpiry: expiryTimestamp,
      })).catch(() => false)
      if (didSelect !== false) {
        cacheSelectedJourneys()
        onClose()
      }
      return
    }

    if (!onSendMessage || isStreaming) return
    cacheSelectedJourneys()

    let userMessage: string
    let contextContent: string

    if (isMultiCity) {
      const groupCurrency = selectedJourneyList[0]?.currencyCode
      const combinedTotal = selectedJourneyList.reduce((sum, journey) => sum + (journey.price.adult.totalFare ?? 0), 0)
      const message = buildMultiCityFlightMessage({ intl, from, to, selectedJourneyList, groupCurrency, combinedTotal, buildFlightContext })
      userMessage = message.userMessage
      contextContent = message.contextContent
    } else if (isReturn && departingJourney && returningJourney) {
      userMessage = intl.formatMessage(
        {
          id: 'ZE1Bw2jDpdHXdHmw312L',
          defaultMessage: 'Add return flight from {from} to {to}',
          description: 'User chat message shown when adding a return flight from the flight search drawer',
        },
        {
          from,
          to,
        },
      )
      contextContent = `Departing flight:\n${buildFlightContext(departingJourney)}\n\nReturning flight:\n${buildFlightContext(returningJourney)}`
    } else {
      userMessage = intl.formatMessage(
        {
          id: 'mtc-4uBZ7D3tkBZV8ueR',
          defaultMessage: 'Add flight from {from} to {to}',
          description: 'User chat message shown when adding a one-way flight from the flight search drawer',
        },
        {
          from,
          to,
        },
      )
      contextContent = buildFlightContext(data.journey)
    }

    const searchMeta = [
      `Search expiry: ${expiryTimestamp}`,
      `Search params: ${searchParams ? JSON.stringify(searchParams) : ''}`,
    ].join('\n')

    const message = `${userMessage}\n<Context label="Flight search result">${contextContent}\n\n${searchMeta}</Context>`
    onSendMessage(message)
    onClose()
  }, [intl, segments, activeSegment, selectedFlights, isReturn, isMultiCity, isCartMode, isCartSubmitting, onCartFlightSelect, onSendMessage, isStreaming, buildFlightContext, onClose, expiryTimestamp, searchParams, cachedSearchId, cachedSessionId])

  const flightsHeading = intl.formatMessage({
    id: '0py3lHxthzacj0uaxcjK',
    defaultMessage: 'Flights',
    description: 'Heading for the flight search drawer in trip planner',
  })

  const savedHeading = intl.formatMessage({
    id: 'UkCP7iQhTsHWmenEq93v',
    defaultMessage: 'Saved',
    description: 'Section heading for the saved flights in the trip planner flight drawer',
  })

  const exploreHeading = intl.formatMessage({
    id: 'hn-UDYbNIkT2Q6CwUXBK',
    defaultMessage: 'Explore more',
    description: 'Section heading above the flight search in the trip planner flight drawer',
  })

  const noResultsMessage = intl.formatMessage({
    id: 'P51jHD0yS8A8bb49PJWq',
    defaultMessage: 'No flights found for your search. Try different dates or airports.',
    description: 'Message shown when no flight results are found in the trip planner drawer',
  })

  const departingFlightLabel = intl.formatMessage({
    id: 'qJfCIiKntFNv9yNLHAHJ',
    defaultMessage: 'Departing flight',
    description: 'Label for departing flight segment in trip planner drawer',
  })

  const returningFlightLabel = intl.formatMessage({
    id: '4p1QhxNAMtAdkUW9nReN',
    defaultMessage: 'Returning flight',
    description: 'Label for returning flight segment in trip planner drawer',
  })

  const resultsHeaderLabel = useMemo(() => {
    if (isMultiCity) {
      return intl.formatMessage(
        {
          id: 'xmce4KMu4_6CYz9hwEpo',
          defaultMessage: 'Flight {flightNumber}',
          description: 'Label for a multi-city flight segment with its sequence number',
        },
        { flightNumber: currentSegmentIndex + 1 },
      )
    }
    if (isReturn && isReturningStep) return returningFlightLabel
    return departingFlightLabel
  }, [intl, isMultiCity, isReturn, isReturningStep, currentSegmentIndex, departingFlightLabel, returningFlightLabel])

  const showResults = hasSearched && !fetching && displayJourneys.length > 0
  const showNoResults = hasSearched && !fetching && !error && displayJourneys.length === 0
  const showError = hasSearched && !fetching && !!error
  const showSavedFlightsSection = hasSavedFlights && !!headerSlot

  return (
    <>
      <SearchWrapper>
        <VerticalSpacer gap={24}>
          <Heading variant="heading2">{flightsHeading}</Heading>

          {showSavedFlightsSection && (
            <VerticalSpacer gap={12}>
              <Heading variant="heading4">{savedHeading}</Heading>
              {headerSlot}
            </VerticalSpacer>
          )}

          <VerticalSpacer gap={16}>
            {showSavedFlightsSection && <Heading variant="heading4">{exploreHeading}</Heading>}

            <CSSBreakpoint min="tablet">
              <FlightSearchWidgetDesktop onSearchAirport={handleSearch} showMultiCity={showMultiCity} groupAirportDropdowns />
            </CSSBreakpoint>
            <CSSBreakpoint max="mobile">
              <VerticalSpacer gap={12} as="form" onSubmit={(e: React.FormEvent) => { e.preventDefault(); handleSearch() }}>
                <FlightSearchWidgetMobile showMultiCity={showMultiCity} />
                <TextButton kind="primary" size="large" type="submit" fit="full-width">
                  {intl.formatMessage({
                    id: 'WGRNTZqigCaFkyNGLUr0',
                    defaultMessage: 'Search',
                    description: 'Search button label to initiate flight search in the trip planner drawer',
                  })}
                </TextButton>
              </VerticalSpacer>
            </CSSBreakpoint>
          </VerticalSpacer>
        </VerticalSpacer>
      </SearchWrapper>

      {hasSearched && searchResultsTimestamp && (
        <CountdownWrapper>
          <FlightSessionCountdownClock />
        </CountdownWrapper>
      )}

      {hasSearched && (isReturn || isMultiCity) && searchParams && (
        <ContentWrapper>
          <StepperProgressSteps activeStepIndex={currentSegmentIndex}>
            {searchParams.itinerary.map((leg, index) => (
              <StepperProgressStep
                key={segments[index]}
                stepIndex={index}
                label={intl.formatMessage(
                  {
                    id: 'AEiFGqvrHSc9lXaMfYat',
                    defaultMessage: '{origin} — {destination}',
                    description: 'Flight route step label showing origin and destination airport codes in trip planner',
                  },
                  { origin: leg.departureAirport, destination: leg.arrivalAirport },
                )}
                onStepClick={selectedFlights[segments[index]] ? handleStepClick : undefined}
                sublabel={selectedFlights[segments[index]] ? getStepSublabel(selectedFlights[segments[index]]) : undefined}
              />
            ))}
          </StepperProgressSteps>
        </ContentWrapper>
      )}

      {fetching && <>
        <FlightsLoadingTopBar />
        <ContentWrapper>
          <LoadingAnimationBox variant="flights" />
        </ContentWrapper>
      </>}

      {showError && (
        <ContentWrapper>
          <BodyText variant="medium" colour="critical">
            {intl.formatMessage({
              id: 'W6e3tDO59GW7SKXKCf3P',
              defaultMessage: 'Something went wrong while searching for flights. Please try again.',
              description: 'Error message when flight search fails in the trip planner drawer',
            })}
          </BodyText>
        </ContentWrapper>
      )}

      {showNoResults && (
        <ContentWrapper>
          <BodyText variant="medium" colour="light-foreground-alt">{noResultsMessage}</BodyText>
        </ContentWrapper>
      )}

      {showResults && (
        <ContentWrapper>
          <VerticalSpacer gap={12}>
            <ChatFlightResultsHeader
              label={resultsHeaderLabel}
              count={visibleJourneys.length}
              sort={flightSort}
              originAirportCode={searchParams?.itinerary[currentSegmentIndex]?.departureAirport}
              destinationAirportCode={searchParams?.itinerary[currentSegmentIndex]?.arrivalAirport}
              onSortChange={setFlightSort}
            />

            <TripFlightFiltersPanel
              filterControls={tripFlightFilters}
              originAirportCode={searchParams?.itinerary[currentSegmentIndex]?.departureAirport ?? ''}
              destinationAirportCode={searchParams?.itinerary[currentSegmentIndex]?.arrivalAirport ?? ''}
            />

            {visibleJourneys.length === 0 && (
              <VerticalSpacer gap={12}>
                <BodyText variant="medium" colour="light-foreground-alt">
                  {intl.formatMessage({
                    id: 'tHI6ugJkg6Xo9BikjkNq',
                    defaultMessage: 'No flights match your filters.',
                    description: 'Message shown when active filters exclude all flight results in the trip planner drawer',
                  })}
                </BodyText>
                <TextButton kind="secondary" size="small" onClick={resetAllFilters}>
                  {intl.formatMessage({
                    id: 'drpXi_9Ybwre8YQMfj49',
                    defaultMessage: 'Reset filters',
                    description: 'Button label to clear flight filters when no results match in the trip planner drawer',
                  })}
                </TextButton>
              </VerticalSpacer>
            )}

            {visibleJourneys.map((journey) => (
              <DrawerFlightCard
                key={journey.id}
                journey={journey}
                onSelect={canSelectFlight ? handleSelectFlight : undefined}
                onViewDetails={() => showModal(<JourneyV2DetailsModal journey={journey} />)}
                isAlreadyAdded={!isCartMode && isJourneyAlreadyAdded(journey, existingFlightFingerprints)}
                isSelectDisabled={isCartMode && isCartSubmitting}
                showSelectButton={canSelectFlight}
                alignColumns
              />
            ))}
          </VerticalSpacer>
        </ContentWrapper>
      )}
    </>
  )
}

function FlightSearchDrawer({
  isOpen,
  onClose,
  originAirportCode,
  originAirportName,
  destinationAirportCode,
  destinationAirportName,
  departureDate,
  returnDate,
  itinerary,
  fareType: fareTypeProp,
  fareClass: fareClassProp,
  occupants,
  passengers,
  autoSearch,
  onSendMessage,
  isStreaming = false,
  onCartFlightSelect,
  isCartSubmitting = false,
  existingFlights,
  headerSlot,
  hasSavedFlights,
  showMultiCity,
}: Props) {
  const { fareTypes, fareCabins } = useFlightFareLabels()

  const existingFlightFingerprints = useMemo(() => {
    const set = new Set<string>()
    if (!existingFlights) return set
    for (const option of existingFlights) {
      const data = option.savedItemData
      if (data?.source === 'le_flight' && data.flightNumber) {
        set.add(getFlightFingerprint(data.flightNumber, data.departureAirport, data.arrivalAirport))
      }
    }
    return set
  }, [existingFlights])

  const widgetInitialState = useMemo((): FlightSearchWidgetState => {
    const resolvedFareType = fareTypeProp
      ? fareTypes.find(ft => ft.value === fareTypeProp)
      : undefined
    const resolvedFareCabin = fareClassProp
      ? fareCabins.find(fc => fc.value === fareClassProp)
      : undefined

    const state: FlightSearchWidgetState = {
      ...initialState,
      fareType: resolvedFareType ?? fareTypes.find(ft => ft.value === FlightsFareTypes.ONE_WAY) ?? fareTypes[0],
      fareCabin: resolvedFareCabin ?? fareCabins[0],
    }

    if (occupants) {
      state.occupants = {
        ...state.occupants,
        ...occupants,
      }
    } else if (passengers && passengers > 0) {
      state.occupants = {
        ...state.occupants,
        adults: passengers,
      }
    }

    if (itinerary?.length && resolvedFareType?.value === FlightsFareTypes.MULTI_CITY) {
      state.flights = itinerary.map(leg => createEmptyFlightSearchWidgetFlightItem({
        id: uuidV4(),
        departureAirport: {
          airportCode: leg.departureAirport,
          airportName: leg.departureAirport,
        } as App.AirportLocation,
        arrivalAirport: {
          airportCode: leg.arrivalAirport,
          airportName: leg.arrivalAirport,
        } as App.AirportLocation,
        checkinDate: moment(leg.departureDate),
      }))
      return state
    }

    if (originAirportCode) {
      state.flights = [{
        ...state.flights[0],
        id: uuidV4(),
        departureAirport: {
          airportCode: originAirportCode,
          airportName: originAirportName ?? originAirportCode,
        } as App.AirportLocation,
      }]
    }

    if (destinationAirportCode && state.flights[0]) {
      state.flights = [{
        ...state.flights[0],
        arrivalAirport: {
          airportCode: destinationAirportCode,
          airportName: destinationAirportName ?? destinationAirportCode,
        } as App.AirportLocation,
      }]
    }

    if (departureDate) {
      state.flights = [{
        ...state.flights[0],
        checkinDate: moment(departureDate),
      }]
    }

    if (returnDate) {
      state.flights = [{
        ...state.flights[0],
        checkoutDate: moment(returnDate),
      }]
    }

    return state
  }, [fareTypes, fareCabins, fareTypeProp, fareClassProp, occupants, passengers, itinerary, originAirportCode, originAirportName, destinationAirportCode, destinationAirportName, departureDate, returnDate])

  const [widgetState, widgetDispatch] = useReducer<FlightSearchWidgetState, [FlightSearchWidgetDispatchAction]>(
    flightSearchWidgetStateReducer,
    widgetInitialState,
  )

  if (!isOpen) return null

  return (
    <TripDrawer
      isOpen={isOpen}
      onClose={onClose}
      direction="right-to-left"
    >
      <FlightSearchWidgetStateProvider state={widgetState} dispatch={widgetDispatch}>
        <FlightSearchDrawerContent
          onSendMessage={onSendMessage}
          isStreaming={isStreaming}
          onCartFlightSelect={onCartFlightSelect}
          isCartSubmitting={isCartSubmitting}
          onClose={onClose}
          existingFlightFingerprints={existingFlightFingerprints}
          autoSearch={autoSearch ?? hasCompleteFlightSearchDefaults({
            originAirportCode,
            destinationAirportCode,
            departureDate,
            returnDate,
            itinerary,
            fareType: fareTypeProp,
          })}
          headerSlot={headerSlot}
          hasSavedFlights={hasSavedFlights}
          showMultiCity={showMultiCity}
        />
      </FlightSearchWidgetStateProvider>
    </TripDrawer>
  )
}

export default FlightSearchDrawer
